// State selection for the pre-recorded (scheduled) stream page.
//
// The preview pane, the setup bar and the Stream control card all describe the
// same thing — can this stream play, and when — so they derive it from the same
// inputs through these helpers instead of each keeping its own flags.

// How long after the scheduled time (or after the last live pulse) the stream is
// given to come up before the page stops saying it's about to start.
export const SCHEDULED_START_GRACE_MS = 2 * 60 * 1000

export const PREVIEW_STATES = Object.freeze({
	Disabled: 'disabled',
	Live: 'live',
	Connecting: 'connecting',
	Reconnecting: 'reconnecting',
	Loading: 'loading',
	NeedsVideos: 'needs-videos',
	Starting: 'starting',
	Countdown: 'countdown',
	Missed: 'missed',
	Ended: 'ended',
	Idle: 'idle',
})

/** Videos the scheduler would actually play: enabled, uploaded, not being removed. */
export function countPlayableVideos(videos) {
	return (videos || []).filter((v) => v && v.enabled === true && !v.file && !v.removing).length
}

/** Total runtime of the playable videos, in ms. */
export function playableRuntimeMs(videos) {
	return (videos || []).reduce((sum, v) => {
		if (!v || v.enabled !== true || v.file || v.removing) return sum
		const secs = v.mediaInfo && v.mediaInfo.duration
		return sum + (secs > 0 ? secs * 1000 : 0)
	}, 0)
}

/**
 * Whether a saved schedule is still ahead of us or running: loop mode, or a
 * date & time whose run window (start + runtime, at least the start grace)
 * hasn't closed.
 *
 * @param {{ mode?: string|null, datetime?: number|null }} [config]
 * @param {number} now
 * @param {number} [runtimeMs]
 */
export function isScheduleActive(config, now, runtimeMs = 0) {
	if (!config) return false
	if (config.mode === 'loop') return true
	if (config.mode !== 'datetime' || !Number.isFinite(config.datetime)) return false
	return now < config.datetime + Math.max(runtimeMs, SCHEDULED_START_GRACE_MS)
}

// Within this long of the scheduled start the playlist is locked, as the
// scheduler is already preparing the run.
export const PLAYLIST_LOCK_BEFORE_START_MS = 60 * 1000

/**
 * Whether playlist editing is locked: while the stream is up or coming up,
 * and in the last minute of a countdown. Everything else (ended, missed,
 * idle, needs videos, disabled, loading) leaves it editable.
 *
 * @param {string} state one of PREVIEW_STATES
 * @param {number|null} [datetime] scheduled start, ms
 * @param {number} now
 */
export function isPlaylistLockedFor(state, datetime, now) {
	const S = PREVIEW_STATES
	if (state === S.Live || state === S.Connecting || state === S.Reconnecting || state === S.Starting) return true
	if (state === S.Countdown) return Number.isFinite(datetime) && datetime - now <= PLAYLIST_LOCK_BEFORE_START_MS
	return false
}

/**
 * Pick the one state the preview pane shows. First match wins:
 *
 *  disabled     stream is off
 *  live         pulse alive and its serving host known (player)
 *  connecting   pulse alive without a host yet
 *  reconnecting pulse dropped within the grace window while the run is still open
 *  loading      playlist not fetched yet (avoids flashing a wrong state)
 *  needs-videos no playable video, whatever the schedule says
 *  starting     loop mode, or the scheduled time was reached within the grace window
 *  countdown    scheduled time is in the future
 *  ended        the run was seen live and is over, or its whole window has passed
 *  missed       past the grace window, inside the run window, never came up
 *  idle         nothing scheduled
 *
 * @param {object} input
 * @param {boolean} input.enabled
 * @param {boolean} input.alive         fresh pulse reports the stream alive
 * @param {boolean} input.hostReady     pulse carries a usable serving host
 * @param {boolean} input.playlistLoaded
 * @param {number}  input.playableCount
 * @param {string|null} [input.mode]    'loop' | 'datetime' | null
 * @param {number|null} [input.datetime] scheduled start, ms
 * @param {number}  [input.runtimeMs]   runtime of the playable videos
 * @param {number|null} [input.lastAliveAt] last time a live pulse was seen for this schedule
 * @param {number}  input.now
 * @returns {string} one of PREVIEW_STATES
 */
export function selectScheduledPreviewState({
	enabled,
	alive,
	hostReady,
	playlistLoaded,
	playableCount,
	mode = null,
	datetime = null,
	runtimeMs = 0,
	lastAliveAt = null,
	now,
}) {
	const S = PREVIEW_STATES
	if (!enabled) return S.Disabled
	if (alive) return hostReady ? S.Live : S.Connecting

	// A drop mid-run gets the grace window to recover; once the run window has
	// closed, a drop is just the run finishing.
	const droppedRecently = Number.isFinite(lastAliveAt) && now - lastAliveAt < SCHEDULED_START_GRACE_MS
	if (droppedRecently && isScheduleActive({ mode, datetime }, now, runtimeMs)) return S.Reconnecting

	if (!playlistLoaded) return S.Loading
	if (!playableCount) return S.NeedsVideos

	if (mode === 'loop') return S.Starting
	if (mode !== 'datetime' || !Number.isFinite(datetime)) return S.Idle

	if (now < datetime) return S.Countdown
	if (Number.isFinite(lastAliveAt)) return S.Ended
	if (now < datetime + SCHEDULED_START_GRACE_MS) return S.Starting
	if (now >= datetime + Math.max(runtimeMs, SCHEDULED_START_GRACE_MS)) return S.Ended
	return S.Missed
}
